/**
 * salesforce-ninja.js
 *
 * Intercepts Ninja Forms submissions, sends data to Pardot via AJAX,
 * updates submission cookies, and replaces content on success.
 */
import {isLoading} from "./common/loader.js";
import popupConfirm from "./common/popupConfirm.js";

(() => {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...args) {
        this._requestUrl = url;
        this._requestMethod = method;
        return originalOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.send = function (body) {
        this._requestBody = body;
        const isNinjaFormsRequest =
            this._requestUrl?.includes("admin-ajax.php") &&
            typeof body === "string" &&
            body.includes("action=nf_ajax_submit");

        this.addEventListener("load", () => {
            if (!isNinjaFormsRequest) return;

            let responseJson;
            try {
                responseJson = JSON.parse(this.responseText);
            } catch {
                isLoading(false);
                showErrorPopup();
                return;
            }

            const formId = responseJson.data?.form_id;
            if (!formId) return;

            const formContainer = document.getElementById(`nf-form-${formId}-cont`);
            if (!formContainer) return;

            const webhookResponse = responseJson.data?.actions?.webhooks?.response ?? {};
            let webhookResult;
            try {
                webhookResult = JSON.parse(webhookResponse.body);
            } catch {
                webhookResult = "";
            }

            const isSuccessfulWebhook = webhookResult === "success";
            if (!isSuccessfulWebhook) {
                isLoading(false);
                showErrorPopup();
                return;
            }

            // Always create cookies from webhook response for ALL forms
            const cookiesToSet = webhookResponse.cookies || [];
            cookiesToSet.forEach(({ name, value, expires, path }) => {
                const expiresDate = new Date(expires * 1000).toUTCString();
                const base = `${name}=${value};expires=${expiresDate};path=${path}`;
                document.cookie = base;
            });

            // Extract visitor_id from the cookies that were just set
            const visitorIdCookie = cookiesToSet.find(cookie => cookie.name === "visitor_id822463");
            const visitorId = visitorIdCookie ? visitorIdCookie.value : null;

            const submissionId = responseJson.data?.actions?.save?.sub_id ?? null;
            const userEmail = responseJson.data?.fields_by_key?.email?.value ?? "";
            const formTitle = responseJson.data?.settings?.title;
            const pageId = document.querySelector(".resource__content")?.dataset.id ?? "";

            // Check if this is a handler-actions form
            const isHandlerActionsForm = formContainer.closest(".handler-actions");

            const dataExists =
                (Array.isArray(responseJson.data) && responseJson.data.length > 0) ||
                (responseJson.data && !Array.isArray(responseJson.data) && Object.keys(responseJson.data).length > 0);

            if (dataExists) {
                isLoading(true);

                // Promise array to handle multiple calls
                const promises = [];

                // 1. ALWAYS call prospect_update if visitor_id exists
                if (visitorId) {
                    const prospectUpdateBody = new URLSearchParams({
                        action: "prospect_update",
                        email: userEmail,
                        visitor_id: visitorId,
                    });

                    promises.push(
                        fetch(fintua.ajaxurl, {
                            method: "POST",
                            headers: { "Content-Type": "application/x-www-form-urlencoded" },
                            body: prospectUpdateBody,
                        })
                    );
                }

                // 2. Call pardot_form_handler ONLY for .handler-actions forms
                if (isHandlerActionsForm) {
                    const handlerBody = new URLSearchParams({
                        action: "pardot_form_handler",
                        submission_id: submissionId,
                        status: isSuccessfulWebhook,
                        form_id: formId,
                        form_name: formTitle,
                        email: userEmail,
                        page_id: pageId,
                    });

                    promises.push(
                        fetch(fintua.ajaxurl, {
                            method: "POST",
                            headers: { "Content-Type": "application/x-www-form-urlencoded" },
                            body: handlerBody,
                        })
                    );
                }

                // Wait for all promises to complete
                Promise.all(promises)
                    .then((responses) => {
                        // Process the pardot_form_handler response for cookie if it exists
                        const handlerResponse = responses.find((_, index) =>
                            (isHandlerActionsForm && visitorId && index === 1) ||
                            (isHandlerActionsForm && !visitorId && index === 0)
                        );

                        if (handlerResponse) {
                            return handlerResponse.json().then((data) => {
                                const cookieValue = data.data?.cookie;
                                if (cookieValue) {
                                    document.cookie = `fintua_submissions=${cookieValue};path=/;max-age=${365 * 24 * 60 * 60}`;
                                }
                                return data;
                            });
                        }
                        return null;
                    })
                    .then(() => {
                        // Handle success submission logic
                        if (isHandlerActionsForm) {
                            handleSuccessSubmission(formId, formTitle);
                        } else if (document.querySelector(".resource__content")) {
                            handleSuccessSubmission(formId, formTitle);
                        } else {
                            isLoading(false);
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        isLoading(false);
                        showErrorPopup();
                    });
            }
        });

        return originalSend.call(this, body);
    };

    const handleSuccessSubmission = (formId, formName) => {
        const container = document.querySelector(".resource__content");
        if (!container) return;

        const template = container.dataset.template ?? "";
        const pageId = container.dataset.id ?? "";
        const pageTitle = document.title ?? "";
        if (!template || !pageId) return;

        isLoading(true);

        sendGa4Event(template, formId, formName, pageTitle, pageId);

        const detailBody = new URLSearchParams({
            action: "submitted_template_part",
            template,
            page_id: pageId,
        });

        fetch(fintua.ajaxurl, {
            method: "POST",
            headers: {
                "Content-Type":
                    "application/x-www-form-urlencoded; charset=UTF-8",
            },
            body: detailBody,
        })
            .then((res) => res.json())
            .then((json) => {
                if (!json.success) {
                    throw new Error(json.data?.message || "Unknown error");
                }
                container.innerHTML = json.data.html;
                window.scrollTo({top: 0, left: 0, behavior: "smooth"});
            })
            .catch(() => {
                showErrorPopup();
            })
            .finally(() => {
                isLoading(false);
            });
    };

    const sendGa4Event = (
        templateName,
        formId,
        formName,
        pageTitle,
        pageId
    ) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "resource_form_submission",
            resource_type: templateName,
            form_id: formId,
            form_name: formName,
            page_title: pageTitle,
            page_id: pageId,
            timestamp: new Date().toISOString(),
        });

        if (
            ["fintuacom.local", "127.0.0.1"].includes(
                window.location.hostname
            )
        ) {
            console.log("GA4 Event Sent:", {
                event: "resource_form_submission",
                resource_type: templateName,
                form_id: formId,
                form_name: formName,
                page_title: pageTitle,
                page_id: pageId,
            });
        }
    };

    const showErrorPopup = () => {
        popupConfirm(
            "Submission Error",
            "There was an error processing your submission.",
            "Please try again or contact support if the problem persists.",
            "Try Again",
            "Contact us"
        ).then((confirmed) => {
            if (!confirmed) {
                window.location.href = '/contact/';
            }
        });
    };

    document
        .querySelector(".is-submitted")
        ?.addEventListener("click", () => handleSuccessSubmission());
})();
